import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import LoginModal from './auth/LoginModal';
import './Navbar.css';

interface NavbarProps {
  onMobileMenuClick?: () => void;
  showHamburger?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ onMobileMenuClick, showHamburger = false }) => {
  const [rightMenuOpen, setRightMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginMode, setLoginMode] = useState<'member' | 'admin'>('member');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  const profileRef = useRef<HTMLDivElement>(null);
  const rightMenuRef = useRef<HTMLDivElement>(null);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.body.classList.toggle('dark-theme', darkMode);
    localStorage.setItem('darkMode', String(darkMode));
  }, [darkMode]);

  // Open admin tab if ?admin=1 in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('ref') === 'sys') {
      setLoginMode('admin');
      setShowLoginModal(true);
    }
  }, [location.search]);

  // Listen for open-login-modal events dispatched by inner pages
  useEffect(() => {
    const handler = () => { setLoginMode('member'); setShowLoginModal(true); };
    window.addEventListener('open-login-modal', handler);
    return () => window.removeEventListener('open-login-modal', handler);
  }, []);

  // Close right menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (rightMenuRef.current && !rightMenuRef.current.contains(e.target as Node)) {
        setRightMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close profile dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openMemberLogin = () => { setLoginMode('member'); setShowLoginModal(true); };

  const handleSignOut = async () => {
    await logout();
    setProfileOpen(false);
    navigate('/');
  };

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  return (
    <>
      <nav className="navbar">
        <div className="nb-inner">
          {/* Left hamburger — always visible, opens left sidebar */}
          <button
            className="nb-hamburger nb-hamburger-left"
            onClick={() => { onMobileMenuClick?.(); setRightMenuOpen(false); }}
            aria-label="Open menu"
          >
            <span className="nb-ham-line"></span>
            <span className="nb-ham-line"></span>
            <span className="nb-ham-line"></span>
          </button>

          {/* Logo */}
          <NavLink to="/" className="nb-logo" onClick={() => setRightMenuOpen(false)}>
            <div className="nb-logo-mark">📈</div>
            <span className="nb-logo-text">SwingRadar<svg className="nb-logo-x" viewBox="0 0 24 24" aria-hidden="true"><path fill="#6366f1" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg><strong>Academy</strong></span>
          </NavLink>

          {/* Desktop links */}
          <div className="nb-links">
            <NavLink to="/" className={({ isActive }) => isActive ? 'nb-link active' : 'nb-link'} end>Home</NavLink>
            {isAuthenticated && (
              <>
                <NavLink to="/courses" className={({ isActive }) => isActive ? 'nb-link active' : 'nb-link'}>Learning</NavLink>
                <NavLink to="/screeners" className={({ isActive }) => isActive ? 'nb-link active' : 'nb-link'}>Screeners</NavLink>
              </>
            )}
            {(!isAuthenticated || !user?.isPremium) && (
              <NavLink to="/pricing" className={({ isActive }) => isActive ? 'nb-link active' : 'nb-link'}>Pricing</NavLink>
            )}
          </div>

          {/* Actions */}
          <div className="nb-actions">
            {/* Show theme toggle in navbar when signed in, in right menu when guest */}
            {isAuthenticated && (
              <button className="nb-theme" onClick={() => setDarkMode(!darkMode)} aria-label="Toggle theme">
                {darkMode ? '🌙' : '☀️'}
              </button>
            )}

            {isAuthenticated && user ? (
              <div className="nb-profile" ref={profileRef}>
                <button className="nb-avatar-btn" onClick={() => setProfileOpen(!profileOpen)}>
                  {user.profilePhoto ? (
                    <img src={user.profilePhoto} alt={user.name} className="nb-avatar-img" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="nb-avatar-placeholder">{initials}</div>
                  )}
                </button>
                {profileOpen && (
                  <div className="nb-profile-menu">
                    <div className="nb-pm-header">
                      <div className="nb-pm-name">{user.name}</div>
                      <div className="nb-pm-email">{user.email || user.username || ''}</div>
                    </div>
                    <div className="nb-pm-plan">
                      <span className="nb-pm-plan-icon">💎</span>
                      <span>{user.isPremium ? (user.planName || 'Pro Member') : 'Free Member'}</span>
                    </div>
                    {user.isPremium && user.expiryDate && (
                      <div className="nb-pm-expiry">
                        Expires: {new Date(user.expiryDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </div>
                    )}
                    {!user.isPremium && (
                      <button className="nb-pm-upgrade" onClick={() => { navigate('/pricing'); setProfileOpen(false); }}>
                        ⚡ Upgrade to Pro
                      </button>
                    )}
                    {user.userType === 'SYSTEM' && (
                      <button className="nb-pm-admin" onClick={() => { navigate('/admin'); setProfileOpen(false); }}>
                        ⚙ Admin Panel
                      </button>
                    )}
                    <button className="nb-pm-signout" onClick={handleSignOut}>Sign Out</button>
                  </div>
                )}
              </div>
            ) : (
              <button className="nb-login-btn" onClick={openMemberLogin}>Sign In</button>
            )}

            {/* Right hamburger — Home + Pricing for guests and non-premium */}
            {(!isAuthenticated || !user?.isPremium) && (
              <div ref={rightMenuRef} style={{ position: 'relative' }}>
                <button
                  className="nb-hamburger nb-hamburger-fries"
                  onClick={() => setRightMenuOpen(!rightMenuOpen)}
                  aria-label="Navigation menu"
                >
                  <span className="nb-fries-wrap">
                    <span className="nb-ham-line"></span>
                    <span className="nb-ham-line nb-ham-line--short"></span>
                    <span className="nb-ham-line"></span>
                  </span>
                </button>
                {rightMenuOpen && (
                  <div className="nb-right-menu">
                    <NavLink to="/" className="nb-mobile-link" onClick={() => setRightMenuOpen(false)} end>Home</NavLink>
                    {(!isAuthenticated || !user?.isPremium) && (
                      <NavLink to="/pricing" className="nb-mobile-link" onClick={() => setRightMenuOpen(false)}>Pricing</NavLink>
                    )}
                    {!isAuthenticated && (
                      <button className="nb-right-menu-theme" onClick={() => setDarkMode(!darkMode)}>
                        {darkMode ? '🌙 Dark mode' : '☀️ Light mode'}
                      </button>
                    )}
                    {!isAuthenticated && (
                      <button className="nb-mobile-login" onClick={() => { openMemberLogin(); setRightMenuOpen(false); }}>Sign In</button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </nav>

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        defaultMode={loginMode}
      />
    </>
  );
};

export default Navbar;
